/**
 * The notification centre — §7.8, roadmap 17.5.
 *
 * ⚠️ **Under `shared/`, not in a portal.** The bell lives in the shell header,
 * which every one of the five surfaces renders. A copy per portal would be five
 * polling intervals against one endpoint and five badges that could disagree.
 *
 * The preference screen is the same module because it reads the same
 * `/me/notification-preferences/` document the bell's own "turn these off" link
 * leads to, and because a change there should invalidate nothing the bell holds
 * — which is easier to keep true when both key factories are in one file.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import { api, unwrap } from '@/shared/api/client'
import type { ApiError } from '@/shared/api/problem'
import type { components } from '@/shared/api/schema'

type Schemas = components['schemas']

export type Notification = Schemas['Notification']
export type NotificationPage = Schemas['PaginatedNotificationList']
export type NotificationPreferences = Schemas['NotificationPreferences']
export type NotificationCategory = Schemas['NotificationCategory']

/**
 * How often the badge asks.
 *
 * ⚠️ **60 seconds, matching the throttle the server sets on this one endpoint.**
 * Every open tab of every signed-in user polls it, so the number is a product
 * decision rather than a tuning knob: shortening it multiplies by the number of
 * tabs in existence. The server's `notification_count` scope allows well above
 * this so a working client is never throttled — what the limit stops is an
 * interval that has degenerated, which is why this constant is exported and
 * asserted in the tests rather than inlined.
 */
export const UNREAD_POLL_MS = 60_000

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (unread: boolean) => ['notifications', 'list', unread] as const,
  unreadCount: () => ['notifications', 'unread-count'] as const,
  preferences: () => ['notifications', 'preferences'] as const,
}

/* ------------------------------------------------------------------- reading */

export function useNotifications(
  options: { unread?: boolean; enabled?: boolean } = {},
): UseQueryResult<NotificationPage, ApiError> {
  const unread = options.unread ?? false

  return useQuery({
    queryKey: notificationKeys.list(unread),
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/notifications/', {
          // `undefined` rather than `false`: the server treats anything but a
          // truthy value as "everything", so sending `unread=false` would be a
          // parameter that reads like a filter and is not one.
          params: { query: unread ? { unread: true } : {} },
        }),
      ),
    // The dropdown is opened deliberately, and what is behind it changed since
    // the badge last counted. Refetching on open is the point.
    staleTime: 0,
    enabled: options.enabled ?? true,
  })
}

/**
 * The badge.
 *
 * ⚠️ **`refetchInterval` rather than a `setInterval` in a component.** An
 * interval registered in an effect is re-registered on every render that misses
 * its dependency array, and the failure mode is additive — two intervals, then
 * four — against the one endpoint that is called without a user action behind
 * it. TanStack owns exactly one timer per query key however many components
 * subscribe, which is what makes the bell safe to render in a shell that five
 * portals mount.
 *
 * `refetchIntervalInBackground` is left at its default of `false` so a tab
 * nobody is looking at stops asking. A laptop shut for a weekend then costs
 * nothing, and the count is refreshed on focus anyway.
 */
export function useUnreadCount(
  options: { enabled?: boolean } = {},
): UseQueryResult<Schemas['NotificationUnreadCount'], ApiError> {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => unwrap(api.GET('/api/v1/notifications/unread-count/', {})),
    refetchInterval: UNREAD_POLL_MS,
    // ⚠️ Anonymous visitors must not poll. The caller passes `enabled` from the
    // session; without it a signed-out browser would ask every minute and get a
    // 401 every minute, each one triggering the client's refresh attempt.
    enabled: options.enabled ?? true,
    // A 401 here means signed out, not a blip. Retrying turns one failure into
    // three, and the client's own refresh-on-401 has already had its go.
    retry: false,
  })
}

/* ------------------------------------------------------------------- writing */

export function useMarkNotificationRead(): UseMutationResult<Notification, ApiError, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/v1/notifications/{id}/read/', { params: { path: { id } } })),
    // ⚠️ Invalidate the **whole** `notifications` tree, not just the list the
    // user was looking at. Marking one read changes the badge, the unfiltered
    // list and the `?unread=true` list, and a screen that updates the row but
    // leaves the badge at 3 is the bug people actually report.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  })
}

export function useMarkAllNotificationsRead(): UseMutationResult<
  Schemas['NotificationReadAll'],
  ApiError,
  void
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/notifications/read-all/', {})),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  })
}

/* --------------------------------------------------------------- preferences */

export function useNotificationPreferences(): UseQueryResult<NotificationPreferences, ApiError> {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () => unwrap(api.GET('/api/v1/me/notification-preferences/', {})),
    staleTime: 0,
  })
}

/**
 * What a PATCH sends — a sparse map, so one switch is one small request.
 *
 * ⚠️ **Derived from the generated contract, never hand-written.** A mirror of a
 * generated type passes `tsc` while it drifts: object spread means a wider
 * object still satisfies a narrower one, so a field the server grew would be
 * accepted at the call site and silently never sent. The organizer hooks carry
 * the same note for the same reason.
 */
export type PreferencePatch = Schemas['PatchedNotificationPreferencesUpdateRequest']

export function useUpdateNotificationPreferences(): UseMutationResult<
  NotificationPreferences,
  ApiError,
  PreferencePatch
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: PreferencePatch) =>
      unwrap(api.PATCH('/api/v1/me/notification-preferences/', { body })),
    // The response *is* the new state, so it is written straight into the cache
    // rather than invalidated and refetched — a checkbox that flickers back to
    // its old value while a refetch is in flight reads as a failed save.
    onSuccess: (data) => queryClient.setQueryData(notificationKeys.preferences(), data),
  })
}
